<template>
  <b-modal ref="modal" size="xl" hide-footer body-class="p-0" title="Membership statement" @hidden="reset">
    <div v-if="loading" class="text-center py-5"><b-spinner variant="primary" /></div>

    <div v-else-if="error" class="alert alert-danger m-3">{{ error }}</div>

    <div v-else-if="data">
      <!-- Toolbar -->
      <div class="d-flex flex-wrap justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
        <date-range-picker
          v-model="range"
          :auto-apply="true"
          :ranges="false"
          :append-to-body="true"
          class="mb-1 mr-2"
        >
          <template #input="picker">
            <span v-if="picker.startDate && picker.endDate">{{ fmtDate(picker.startDate) }} – {{ fmtDate(picker.endDate) }}</span>
            <span v-else class="text-secondary"><i class="bi bi-calendar-event mr-1"></i>All time</span>
          </template>
        </date-range-picker>
        <div class="mb-1">
          <button v-if="range.startDate" class="btn btn-outline-secondary mr-1" @click="clearRange">All time</button>
          <button class="btn btn-primary" @click="print"><i class="bi bi-printer mr-1"></i>Print / PDF</button>
        </div>
      </div>

      <!-- Printable statement -->
      <div ref="sheet" class="p-3 p-md-4 bg-white">
        <div class="d-flex flex-wrap justify-content-between align-items-start border-bottom pb-3 mb-3">
          <div class="mb-2">
            <div class="text-secondary small text-uppercase">Membership statement</div>
            <h4 class="font-weight-bold text-primary mb-1">{{ data.customer.name }}</h4>
            <div class="text-secondary small">
              <span v-if="data.customer.phone"><i class="bi bi-telephone mr-1"></i>{{ data.customer.phone }}</span>
              <span v-if="data.customer.email" class="ml-2"><i class="bi bi-envelope mr-1"></i>{{ data.customer.email }}</span>
            </div>
          </div>
          <div class="text-md-right mb-2">
            <div class="font-weight-bold text-dark">{{ data.company.name }}</div>
            <div v-if="data.company.phone" class="text-secondary small">{{ data.company.phone }}</div>
            <div v-if="data.company.krapin" class="text-secondary small">PIN {{ data.company.krapin }}</div>
            <div class="text-secondary small">
              {{ data.period.from ? fmtDate(data.period.from) + ' – ' + fmtDate(data.period.to) : 'All time' }}
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div class="row mb-4" style="row-gap:16px;">
          <div v-for="k in summary" :key="k.label" class="col-6 col-md-3">
            <div class="text-secondary small mb-1">{{ k.label }}</div>
            <div class="h5 font-weight-bold mb-0" :class="k.cls || 'text-dark'">{{ k.value }}</div>
          </div>
        </div>

        <!-- Lines -->
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th class="text-right">Charge</th>
                <th class="text-right">Payment</th>
                <th class="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="data.period.from" class="text-secondary">
                <td>{{ fmtDate(data.period.from) }}</td>
                <td>Opening balance</td>
                <td></td><td></td>
                <td class="text-right">{{ money(data.summary.opening_balance) }}</td>
              </tr>
              <tr v-for="(l, i) in data.lines" :key="i">
                <td class="text-nowrap">{{ fmtDate(l.date) }}</td>
                <td>
                  <div class="text-dark">{{ l.description }}</div>
                  <small class="text-secondary">
                    {{ l.reference }}<template v-if="l.period"> · {{ l.period }}</template>
                  </small>
                </td>
                <td class="text-right text-nowrap">{{ l.debit ? money(l.debit) : '' }}</td>
                <td class="text-right text-nowrap text-primary">{{ l.credit ? money(l.credit) : '' }}</td>
                <td class="text-right text-nowrap font-weight-bold" :class="l.balance > 0 ? 'text-danger' : 'text-dark'">{{ money(l.balance) }}</td>
              </tr>
              <tr v-if="!data.lines.length">
                <td colspan="5" class="text-center text-secondary py-4">No activity in this period.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-weight-bold">
                <td colspan="2" class="text-right">Totals</td>
                <td class="text-right">{{ money(data.summary.charged) }}</td>
                <td class="text-right text-primary">{{ money(data.summary.paid) }}</td>
                <td class="text-right" :class="data.summary.closing_balance > 0 ? 'text-danger' : 'text-dark'">{{ money(data.summary.closing_balance) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p class="text-secondary small mt-3 mb-0">
          Balance = charges minus payments. A positive balance is owed by the member; wallet balance is shown separately.
        </p>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'

/** Per-member statement: charges (sales + renewals), payments (incl. wallet), running balance, visits. */
export default {
  name: 'MemberStatementModal',

  components: { DateRangePicker },

  data() {
    return {
      customerId: null,
      loading:    false,
      error:      '',
      data:       null,
      range:      { startDate: null, endDate: null },
    }
  },

  computed: {
    summary() {
      const s = this.data.summary
      return [
        { label: 'Charged',        value: this.money(s.charged) },
        { label: 'Paid',           value: this.money(s.paid), cls: 'text-primary' },
        { label: 'Balance due',    value: this.money(s.closing_balance), cls: s.closing_balance > 0 ? 'text-danger' : 'text-dark' },
        { label: 'Wallet',         value: this.money(s.wallet_balance) },
        { label: 'Current plan',   value: s.current_package ? `${s.current_package}` : 'None' },
        { label: 'Valid until',    value: s.current_until ? this.fmtDate(s.current_until) : '—' },
        { label: 'Visits',         value: s.visits + (s.last_visit ? ` · last ${moment(s.last_visit).fromNow()}` : '') },
        { label: 'Member since',   value: s.member_since ? this.fmtDate(s.member_since) : '—' },
      ]
    },
  },

  watch: {
    range(val, old) {
      if (this.data && (val.startDate !== old.startDate || val.endDate !== old.endDate)) this.load()
    },
  },

  methods: {
    show(customerId) {
      this.customerId = customerId
      this.$refs.modal.show()
      this.load()
    },

    async load() {
      if (!this.customerId) return
      this.loading = true
      this.error   = ''
      try {
        const params = {}
        if (this.range.startDate) params.date_from = moment(this.range.startDate).format('YYYY-MM-DD')
        if (this.range.endDate)   params.date_to   = moment(this.range.endDate).format('YYYY-MM-DD')
        const { data } = await axios.get(`/api/subscriptions/customers/${this.customerId}/statement`, { params })
        this.data = data
      } catch (e) {
        this.error = e.response?.status === 403
          ? "You don't have permission to view member statements."
          : 'Could not load the statement.'
      } finally {
        this.loading = false
      }
    },

    clearRange() {
      this.range = { startDate: null, endDate: null }
    },

    print() {
      const css = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map(l => `<link rel="stylesheet" href="${l.href}">`).join('')
      const w = window.open('', '_blank')
      if (!w) return
      w.document.write(`<!DOCTYPE html><html><head><title>Statement - ${this.data.customer.name}</title>${css}</head>
        <body class="bg-white">${this.$refs.sheet.outerHTML}</body></html>`)
      w.document.close()
      w.onload = () => { w.focus(); w.print() }
    },

    reset() {
      this.data  = null
      this.range = { startDate: null, endDate: null }
    },


    fmtDate(d) { return d ? moment(d).format('D MMM YYYY') : '' },
    money(v)   { return 'KES ' + Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
  },
}
</script>
